import { useEffect, useState, type FormEvent } from "react";
import { useI18n, type StringKey } from "../i18n";
import {
  getSetting, loadCustomFields, loadDocumentTypes, saveCustomField, saveDocumentType, saveSetting, toKey,
  type CustomFieldDef, type DocumentType,
} from "../data";
import type { Workspace } from "../types";

export function Settings({ ws }: { ws: Workspace }) {
  return (
    <div className="page">
      {ws.enabled.has("leave") && ws.enabled.has("approvals") && <ApprovalChain ws={ws} />}
      <CustomFields ws={ws} />
      {ws.enabled.has("documents") && <DocumentTypes ws={ws} />}
    </div>
  );
}

const TYPES: CustomFieldDef["field_type"][] = ["text", "number", "date", "boolean", "select"];

function CustomFields({ ws }: { ws: Workspace }) {
  const { t, pick } = useI18n();
  const companyId = ws.membership.company_id;
  const [rows, setRows] = useState<CustomFieldDef[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ label_en: "", label_ar: "", field_type: "text" as CustomFieldDef["field_type"], entity: "person" as CustomFieldDef["entity"], options: "" });
  const [error, setError] = useState<string | null>(null);

  const refresh = () => loadCustomFields(companyId).then(setRows).catch((e) => setError(e.message));
  useEffect(() => { void refresh(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function add(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const options = f.field_type === "select" ? f.options.split(",").map((o) => o.trim()).filter(Boolean) : null;
      await saveCustomField(companyId, {
        label_en: f.label_en.trim(), label_ar: f.label_ar.trim() || null, field_type: f.field_type,
        entity: f.entity, key: toKey(f.label_en), options, sort_order: (rows?.length ?? 0) * 10 + 10,
      });
      setF({ label_en: "", label_ar: "", field_type: "text", entity: "person", options: "" });
      setAdding(false);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
    }
  }

  async function toggle(def: CustomFieldDef, patch: Partial<CustomFieldDef>) {
    setError(null);
    try {
      await saveCustomField(companyId, { id: def.id, ...patch });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <h2>{t("customFields")}</h2>
        {!adding && <button type="button" className="btn primary small" onClick={() => setAdding(true)}>{t("addField")}</button>}
      </div>
      <p className="muted">{t("customFieldsHelp")}</p>
      {adding && (
        <form className="inline-form" onSubmit={add}>
          <div className="field-row">
            <label className="field"><span>{t("fieldLabel")}</span>
              <input id="cf-label" dir="ltr" value={f.label_en} onChange={(e) => setF({ ...f, label_en: e.target.value })} required autoFocus /></label>
            <label className="field"><span>{t("fieldLabelAr")}</span>
              <input id="cf-label-ar" dir="rtl" value={f.label_ar} onChange={(e) => setF({ ...f, label_ar: e.target.value })} /></label>
          </div>
          <div className="field-row">
            <label className="field"><span>{t("fieldType")}</span>
              <select id="cf-type" value={f.field_type} onChange={(e) => setF({ ...f, field_type: e.target.value as CustomFieldDef["field_type"] })}>
                {TYPES.map((ty) => <option key={ty} value={ty}>{t(`type_${ty}` as StringKey)}</option>)}
              </select></label>
            <label className="field"><span>{t("appliesToRecord")}</span>
              <select id="cf-entity" value={f.entity} onChange={(e) => setF({ ...f, entity: e.target.value as CustomFieldDef["entity"] })}>
                <option value="person">{t("entity_person")}</option>
                <option value="employment">{t("entity_employment")}</option>
              </select></label>
          </div>
          {f.field_type === "select" && (
            <label className="field"><span>{t("fieldOptions")}</span>
              <input id="cf-options" value={f.options} onChange={(e) => setF({ ...f, options: e.target.value })} placeholder="Morning, Evening, Night" required /></label>
          )}
          <div className="actions">
            <button type="button" className="btn secondary" onClick={() => setAdding(false)}>{t("cancel")}</button>
            <button className="btn primary" disabled={!f.label_en.trim()}>{t("save")}</button>
          </div>
        </form>
      )}
      {error && <p className="alert danger" role="alert">{error}</p>}
      {rows && rows.length === 0 && !adding && <p className="muted">{t("noFields")}</p>}
      {rows && rows.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead><tr><th>{t("fieldLabel")}</th><th>{t("fieldType")}</th><th>{t("appliesToRecord")}</th><th>{t("required")}</th><th>{t("activeLabel")}</th></tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} className={d.active ? "" : "muted"}>
                  <td>{pick(d.label_en, d.label_ar)}{d.options ? <div className="muted small-note">{d.options.join(", ")}</div> : null}</td>
                  <td>{t(`type_${d.field_type}` as StringKey)}</td>
                  <td>{t(`entity_${d.entity}` as StringKey)}</td>
                  <td><input type="checkbox" id={`cf-req-${d.id}`} aria-label={t("required")} checked={d.required} onChange={(e) => void toggle(d, { required: e.target.checked })} /></td>
                  <td><input type="checkbox" id={`cf-act-${d.id}`} aria-label={t("activeLabel")} checked={d.active} onChange={(e) => void toggle(d, { active: e.target.checked })} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const APPLIES: DocumentType["applies_to"][] = ["all", "male", "female", "egyptian", "non_egyptian"];

function DocumentTypes({ ws }: { ws: Workspace }) {
  const { t, pick } = useI18n();
  const companyId = ws.membership.company_id;
  const [rows, setRows] = useState<DocumentType[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name_en: "", name_ar: "", applies_to: "all" as DocumentType["applies_to"], has_expiry: false });
  const [error, setError] = useState<string | null>(null);

  const refresh = () => loadDocumentTypes(companyId).then(setRows).catch((e) => setError(e.message));
  useEffect(() => { void refresh(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save(patch: Partial<DocumentType> & { id?: string }) {
    setError(null);
    try {
      await saveDocumentType(companyId, patch);
      await refresh();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
      return false;
    }
  }

  async function add(e: FormEvent) {
    e.preventDefault();
    const ok = await save({
      key: toKey(f.name_en), name_en: f.name_en.trim(), name_ar: f.name_ar.trim() || null, applies_to: f.applies_to,
      has_expiry: f.has_expiry, required_for_hire: true, sort_order: (rows?.length ?? 0) * 10 + 200,
    });
    if (ok) { setF({ name_en: "", name_ar: "", applies_to: "all", has_expiry: false }); setAdding(false); }
  }

  return (
    <section className="card">
      <div className="card-head">
        <h2>{t("hiringDocsList")}</h2>
        {!adding && <button type="button" className="btn primary small" onClick={() => setAdding(true)}>{t("addDocument")}</button>}
      </div>
      <p className="muted">{t("hiringDocsHelp")}</p>
      {adding && (
        <form className="inline-form" onSubmit={add}>
          <div className="field-row">
            <label className="field"><span>{t("documentName")}</span>
              <input id="dt-name" dir="ltr" value={f.name_en} onChange={(e) => setF({ ...f, name_en: e.target.value })} required autoFocus /></label>
            <label className="field"><span>{t("documentNameAr")}</span>
              <input id="dt-name-ar" dir="rtl" value={f.name_ar} onChange={(e) => setF({ ...f, name_ar: e.target.value })} /></label>
            <label className="field"><span>{t("appliesTo")}</span>
              <select id="dt-applies" value={f.applies_to} onChange={(e) => setF({ ...f, applies_to: e.target.value as DocumentType["applies_to"] })}>
                {APPLIES.map((a) => <option key={a} value={a}>{t(`applies_${a}` as StringKey)}</option>)}
              </select></label>
          </div>
          <label className="check"><input id="dt-expiry" type="checkbox" checked={f.has_expiry} onChange={(e) => setF({ ...f, has_expiry: e.target.checked })} /> {t("hasExpiry")}</label>
          <div className="actions">
            <button type="button" className="btn secondary" onClick={() => setAdding(false)}>{t("cancel")}</button>
            <button className="btn primary" disabled={!f.name_en.trim()}>{t("save")}</button>
          </div>
        </form>
      )}
      {error && <p className="alert danger" role="alert">{error}</p>}
      {rows && (
        <div className="table-wrap">
          <table>
            <thead><tr><th>{t("documents")}</th><th>{t("appliesTo")}</th><th>{t("required")}</th><th>{t("activeLabel")}</th></tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} className={d.active ? "" : "muted"}>
                  <td>{pick(d.name_en, d.name_ar)}</td>
                  <td>
                    <select id={`dt-app-${d.id}`} aria-label={t("appliesTo")} value={d.applies_to} onChange={(e) => void save({ id: d.id, applies_to: e.target.value as DocumentType["applies_to"] })}>
                      {APPLIES.map((a) => <option key={a} value={a}>{t(`applies_${a}` as StringKey)}</option>)}
                    </select>
                  </td>
                  <td><input type="checkbox" id={`dt-req-${d.id}`} aria-label={t("required")} checked={d.required_for_hire} onChange={(e) => void save({ id: d.id, required_for_hire: e.target.checked })} /></td>
                  <td><input type="checkbox" id={`dt-act-${d.id}`} aria-label={t("activeLabel")} checked={d.active} onChange={(e) => void save({ id: d.id, active: e.target.checked })} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const CHAINS: { key: StringKey; value: string[] }[] = [
  { key: "chain_manager_hr", value: ["manager", "hr"] },
  { key: "chain_manager", value: ["manager"] },
  { key: "chain_hr", value: ["hr"] },
  { key: "chain_none", value: [] },
];

function ApprovalChain({ ws }: { ws: Workspace }) {
  const { t } = useI18n();
  const companyId = ws.membership.company_id;
  const [current, setCurrent] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void getSetting(companyId, "leave.approval_chain").then((v) => setCurrent(JSON.stringify(v ?? ["manager", "hr"])));
  }, [companyId]);

  async function choose(value: string[]) {
    setError(null);
    setSaved(false);
    try {
      await saveSetting(companyId, "leave.approval_chain", value);
      setCurrent(JSON.stringify(value));
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
    }
  }

  return (
    <section className="card">
      <div className="card-head"><h2>{t("approvalChain")}</h2>{saved && <span className="badge success">{t("saved")}</span>}</div>
      <div className="radio-list" role="radiogroup" aria-label={t("approvalChain")}>
        {CHAINS.map((c) => (
          <label key={c.key} className="check">
            <input type="radio" name="chain" id={`chain-${c.key}`} checked={current === JSON.stringify(c.value)} onChange={() => choose(c.value)} />
            {t(c.key)}
          </label>
        ))}
      </div>
      <p className="muted small-note">{t("approvalChainHelp")}</p>
      {error && <p className="alert danger" role="alert">{error}</p>}
    </section>
  );
}
